// Conteúdo EFETIVAMENTE enviado de UM canal (sentTemplate SINGULAR do
// próprio canal): EMAIL → subject + HTML renderizado SANITIZADO
// (DOMPurify, padrão do editor) num frame; WHATSAPP/SMS → bolha de
// chat (mesmo estilo do template-preview do editor) com o texto do
// JSON. A seção linka o template@versão para o editor.
import DOMPurify from "dompurify"
import { ExternalLinkIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { Badge } from "@/components/ui/badge"
import type { NotificationSentTemplateType } from "@/types/notification-center"

const sentPrefix =
  "pages.notificationCenter.notificationDetailPage.sentTemplates"

// renderedContent de WHATSAPP/SMS vem como JSON {"text": …} — cai
// no conteúdo cru se não for JSON válido (contrato defensivo).
export const parseSentTemplateText = (
  renderedContent: string
): string => {
  try {
    const parsed: unknown = JSON.parse(renderedContent)
    if (
      parsed &&
      typeof parsed === "object" &&
      "text" in parsed &&
      typeof (parsed as { text: unknown }).text === "string"
    ) {
      return (parsed as { text: string }).text
    }
  } catch {
    // Não era JSON — usa o conteúdo como texto puro.
  }
  return renderedContent
}

// Bolha de chat (mesmo visual do preview WHATSAPP do editor).
const ChatBubble = ({ text }: { text: string }) => (
  <div className="flex flex-col gap-2 rounded-lg bg-[#e5ddd5] p-4">
    <div className="max-w-[85%] self-start rounded-lg rounded-tl-none bg-white p-3 text-sm shadow-sm">
      <p className="whitespace-pre-wrap break-words">
        {text}
      </p>
    </div>
  </div>
)

const SentTemplateSection = ({
  sentTemplate,
  whiteLabelId,
}: {
  sentTemplate: NotificationSentTemplateType
  whiteLabelId: string
}) => {
  const { t } = useTranslation()
  const isEmail = sentTemplate.channel === "EMAIL"

  return (
    <section
      aria-label={`${sentTemplate.channel} — ${sentTemplate.templateName}`}
      className="space-y-2 rounded-md border p-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Badge
          variant="outline"
          className="rounded-full font-medium"
        >
          {sentTemplate.channel}
        </Badge>
        <Link
          to={`/notification-center/configurations/${whiteLabelId}/template-configuration/${sentTemplate.templateId}`}
          className="inline-flex items-center gap-1 text-sm text-primary underline-offset-2 hover:underline"
        >
          {sentTemplate.templateName}
          <span className="tabular-nums">
            @v{sentTemplate.version}
          </span>
          <ExternalLinkIcon
            className="size-3.5"
            aria-hidden="true"
          />
        </Link>
      </div>
      {isEmail ? (
        <div className="space-y-2">
          {sentTemplate.subject && (
            <p className="text-sm">
              <span className="font-medium">
                {t(`${sentPrefix}.subject`)}:
              </span>{" "}
              {sentTemplate.subject}
            </p>
          )}
          <div
            data-testid={`sent-template-html-${sentTemplate.templateId}`}
            className="rounded-md border bg-white p-4 text-sm shadow-sm"
            // Conteúdo sanitizado — nunca renderizar HTML cru
            // (XSS via template comprometido).
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(
                sentTemplate.renderedContent
              ),
            }}
          />
        </div>
      ) : (
        <ChatBubble
          text={parseSentTemplateText(
            sentTemplate.renderedContent
          )}
        />
      )}
    </section>
  )
}

export const NotificationSentTemplates = ({
  sentTemplate,
  whiteLabelId,
}: {
  // sentTemplate SINGULAR do próprio canal — null/undefined quando o
  // envio não produziu conteúdo (ex.: falhou antes de renderizar).
  sentTemplate?: NotificationSentTemplateType | null
  whiteLabelId: string
}) => {
  const { t } = useTranslation()

  if (!sentTemplate) {
    return (
      <p className="rounded-md border p-6 text-center text-sm text-muted-foreground">
        {t(`${sentPrefix}.empty`)}
      </p>
    )
  }

  return (
    <div className="py-2">
      <SentTemplateSection
        sentTemplate={sentTemplate}
        whiteLabelId={whiteLabelId}
      />
    </div>
  )
}
